import React from 'react';
import Link from 'next/link';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';

export interface PageHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  /** Rendered inline after the title (e.g. a status `Badge`). */
  badge?: React.ReactNode;
  /** Row under the title/description: small muted text/icons (dates, venue). */
  meta?: React.ReactNode;
  /** Small "← label" link above the title. */
  backAction?: { href: string; label: string };
}

export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  (
    { className, title, description, children, actions, badge, meta, backAction, ...props },
    ref,
  ) => {
    const heading = <h1 className="font-heading text-2xl font-bold tracking-tight">{title}</h1>;
    return (
      <div ref={ref} className={cn('space-y-2', className)} {...props}>
        {backAction && (
          <Link
            href={backAction.href}
            aria-label={backAction.label}
            className="text-muted hover:text-foreground inline-flex items-center gap-1 text-sm"
          >
            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
            {backAction.label}
          </Link>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {badge ? (
              <div className="flex flex-wrap items-center gap-2">
                {heading}
                {badge}
              </div>
            ) : (
              heading
            )}
            {description && <p className="text-muted text-sm">{description}</p>}
            {meta && (
              <div className="text-muted flex flex-wrap gap-x-4 gap-y-1 text-sm">{meta}</div>
            )}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
        {children}
      </div>
    );
  },
);
PageHeader.displayName = 'PageHeader';
